import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe, ArrowLeft, User, CreditCard, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [billingBusy, setBillingBusy] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({ age_range: "", employment_status: "", monthly_income: "", total_savings: "", preferred_regions: [], timeline: "", language_comfort: "" });
  const [showDelete, setShowDelete] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Accounts created with Google have no password; those confirm by typing DELETE.
  const hasPassword = user?.has_password !== false;

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.deleteAccount(hasPassword ? { password: deleteInput } : { confirm: deleteInput });
      toast.success("Your account has been deleted.");
      navigate("/");
    } catch (error) {
      toast.error(error.message || "Could not delete account");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const [userData, profileData] = await Promise.all([api.getMe(), api.getProfile()]);
        setUser(userData); setProfile(profileData);
      } catch (error) { toast.error("Unable to load profile"); }
      finally { setIsLoading(false); }
    };
    loadProfile();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try { await api.updateProfile(profile); toast.success("Profile updated!"); } catch (error) { toast.error("Failed to save profile"); }
    finally { setIsSaving(false); }
  };

  const openBilling = async () => {
    setBillingBusy(true);
    try {
      const { url } = await api.createPortalSession();
      window.location.href = url;
    } catch (error) {
      toast.error(error.message || "Could not open billing");
      setBillingBusy(false);
    }
  };

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const expiryText = formatDate(user?.core_access_expires);
  const hasPlan = !!user?.has_core_access;
  const cancelling = !!user?.cancel_at_period_end;
  const subStatus = user?.subscription_status;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b p-4"><div className="max-w-7xl mx-auto flex items-center justify-between"><div className="flex items-center gap-4"><Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft /></Button><div className="flex items-center gap-2"><Globe className="h-6 w-6 text-primary" /><span className="font-bold text-lg font-['Manrope']">Nomad Roadmap</span></div></div><Link to="/dashboard"><Button variant="outline" size="sm">Dashboard</Button></Link></div></header>
      <main className="max-w-2xl mx-auto px-4 py-8">
        <Card className="border-border/50 mb-6"><CardContent className="p-6"><div className="flex items-center gap-4"><div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center"><User className="h-8 w-8 text-primary" /></div><div><h2 className="text-xl font-semibold">{user?.name}</h2><p className="text-muted-foreground">{user?.email}</p></div></div></CardContent></Card>

        <Card className="border-border/50 mb-6">
          <CardHeader><CardTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-primary" />Your plan</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {hasPlan ? (
              <>
                <p className="text-sm">
                  <span className="font-semibold">Nomad Roadmap</span>
                  {subStatus === "trialing" && " - first 3 months"}
                  {subStatus === "active" && " - monthly"}
                  {subStatus === "past_due" && " - payment issue"}
                  {subStatus === "canceled" && " - cancelled"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {cancelling || subStatus === "canceled"
                    ? `Access ends ${expiryText || "at the end of this period"}.`
                    : expiryText
                    ? `Next renewal ${expiryText} at $6.99.`
                    : "Active."}
                </p>
                {subStatus === "past_due" && (
                  <p className="text-sm text-destructive">Your last payment didn't go through. Update your card to keep access.</p>
                )}
                <Button onClick={openBilling} disabled={billingBusy} variant="outline" className="w-full">
                  {billingBusy ? "Opening..." : "Manage billing"}
                </Button>
                <p className="text-xs text-muted-foreground">Update your card, see invoices, or cancel. Handled securely by Stripe.</p>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">No active plan. $29.99 for your first 3 months, then $6.99/month.</p>
                <Link to="/pricing"><Button className="w-full bg-primary hover:bg-primary/90">See the plan</Button></Link>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/50"><CardHeader><CardTitle>Your Profile</CardTitle></CardHeader><CardContent className="space-y-6">
          <div className="space-y-2"><Label>Monthly Income</Label><Select value={profile.monthly_income} onValueChange={val => setProfile({...profile, monthly_income: val})}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="under_1000">Under $1,000</SelectItem><SelectItem value="1000_2000">$1,000 - $2,000</SelectItem><SelectItem value="2000_3000">$2,000 - $3,000</SelectItem><SelectItem value="3000_5000">$3,000 - $5,000</SelectItem><SelectItem value="5000_10000">$5,000 - $10,000</SelectItem><SelectItem value="over_10000">Over $10,000</SelectItem></SelectContent></Select></div>
          <Button onClick={handleSave} disabled={isSaving} className="w-full bg-primary hover:bg-primary/90">{isSaving ? "Saving..." : "Save Changes"}</Button>
        </CardContent></Card>

        <Card className="border-destructive/30 mt-6">
          <CardHeader><CardTitle className="flex items-center gap-2 text-destructive"><Trash2 className="h-5 w-5" />Delete account</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Permanently deletes your account, profile, roadmaps, reminders and every document in your vault, and cancels your subscription. This cannot be undone.
            </p>
            {!showDelete ? (
              <Button variant="outline" className="w-full border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => setShowDelete(true)}>
                Delete my account
              </Button>
            ) : (
              <div className="space-y-3">
                <Label htmlFor="delete-confirm">{hasPassword ? "Enter your password to confirm" : 'Type DELETE to confirm'}</Label>
                <Input
                  id="delete-confirm"
                  type={hasPassword ? "password" : "text"}
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setShowDelete(false); setDeleteInput(""); }} disabled={deleting}>Cancel</Button>
                  <Button variant="destructive" className="flex-1" onClick={handleDeleteAccount} disabled={deleting || !deleteInput}>
                    {deleting ? "Deleting..." : "Permanently delete"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProfilePage;
